import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import MaintenancePage from './pages/Maintenance';
import axios from 'axios';
import { FEATURES } from './config/features';
import CookieConsentBanner, { useCookieConsent } from './components/CookieConsentBanner';
import MetaPixelLoader from './components/MetaPixelLoader';
import GtagLoader from './components/GtagLoader';
import WelcomeActivationModal from './components/WelcomeActivationModal';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { CartProvider } from './contexts/CartContext';
import { FoodCartProvider } from './contexts/FoodCartContext';
import FoodGate from './pages/Food/FoodGate';
import { ToastProvider } from './contexts/ToastContext';

// Layout
import { Main, ConditionalMain } from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import RecoverPassword from './pages/RecoverPassword';
import ResetPassword from './pages/ResetPassword';
import {
  Login,
  Register,
  Dashboard,
  Wallet,
  Pulseira,
  Transactions,
  Profile,
  Payments,
  PaymentResult,
  Ecommerce,
  StoreDetail,
  Checkout,
  Orders,
  ProductDetail,
  Insights,
  Eventos,
  Doacoes,
  Vouchers,
  Developers,
  // Banking Pages
  Banking,
  BankingTransfer,
  BankingBills,
  KYC,
  // Portal Pages
  PortalDashboard,
  PortalProducts,
  PortalServices,
  PortalPayments,
  PortalReports,
  PortalEvents,
  PortalDonations,
  PortalApiDashboard,
  PortalCategories,
  PortalOrders,
  PortalInventory,
  PortalVariations,
  PortalAnnouncements,
  PortalVouchers,
  PortalVoucherScanner,
  PortalStoreProfile,
  PortalBilling,
  BusinessRegister,
  // Food Pages (Pagali Food)
  Food,
  FoodRestaurant,
  FoodCart,
  FoodOrders,
  // Driver Pages (Pagali Driver)
  DriverDashboard,
  DriverDelivery,
  DriverHistory,
  DriverProfile,
  DriverRegister,
} from './pages';
import Academia from './pages/Academia';
import Hub from './pages/Hub';
import Cashback from './pages/Cashback';
import CentralDeAjuda from './pages/CentralDeAjuda';
import EcommercePayment from './pages/EcommercePayment';
import InteresseEntidade from './pages/InteresseEntidade';
import PagaMe from './pages/PagaMe';
import MeusBilhetes from './pages/MeusBilhetes';
import MeusDownloads from './pages/MeusDownloads';
import EbookReader from './pages/EbookReader';
import StorePublic from './pages/StorePublic';
import Notifications from './pages/Notifications';
import EventoLanding from './pages/EventoLanding';

// Styles
import './styles/tailwind.css';

// Wrappers de tracking — dentro do Router para ter acesso ao useLocation
const PixelWrapper: React.FC = () => {
  const { consent } = useCookieConsent();
  const hasConsent = consent === 'granted';
  return (
    <>
      <MetaPixelLoader hasConsent={hasConsent} />
      <GtagLoader hasConsent={hasConsent} />
    </>
  );
};

// Landing Page Redirect Component
const LandingRedirect: React.FC = () => {
  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) {
      window.location.href = 'https://apps.apple.com/cv/app/pagali/id6449363463';
    } else if (/Android/i.test(ua)) {
      window.location.href = 'https://play.google.com/store/apps/details?id=cv.isone.pagali';
    } else {
      window.location.href = '/landing/dist/index.html';
    }
  }, []);
  return null;
};

const App: React.FC = () => {
  const [maintenance, setMaintenance] = useState<{ active: boolean; titulo?: string; mensagem?: string; dataFim?: string } | null>(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const version = process.env.REACT_APP_API_VERSION || 'v1';
    axios.get(`${apiUrl}/${version}/public/maintenance/frontend`, { timeout: 5000 })
      .then(({ data }) => {
        if (data.maintenance) {
          setMaintenance({ active: true, titulo: data.titulo, mensagem: data.mensagem, dataFim: data.data_fim_prevista });
        } else {
          setMaintenance({ active: false });
        }
      })
      .catch(() => setMaintenance({ active: false }));
  }, []);

  if (maintenance === null) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div className="spinner-lg" />
    </div>
  );
  if (maintenance.active) {
    return <MaintenancePage titulo={maintenance.titulo} mensagem={maintenance.mensagem} dataFimPrevista={maintenance.dataFim} />;
  }

  return (
    <Router>
      <PixelWrapper />
      <CookieConsentBanner />
      <ToastProvider>
      <AuthProvider>
        <WelcomeActivationModal />
        <UserProvider>
          <CartProvider>
          <FoodCartProvider>
            <Routes>
                {/* Landing Page - Home */}
                <Route path="/" element={<LandingRedirect />} />

                {/* Public Routes — with sidebar if logged in */}
                <Route element={<ConditionalMain />}>
                  <Route path="/loja/:slug" element={<StorePublic />} />
                  <Route path="/ecommerce" element={<Ecommerce />} />
                  <Route path="/ecommerce/store/:storeId" element={<StoreDetail />} />
                  <Route path="/ecommerce/store/:storeId/product/:productId" element={<ProductDetail />} />
                  <Route path="/ecommerce/checkout" element={<Checkout />} />
                  {/* Pagali Food: montra e restaurantes PÚBLICOS (campanha) — o login só se pede
                      ao encomendar. O interruptor do Admin decide se abre ou mostra Brevemente. */}
                  <Route element={<FoodGate />}>
                    <Route path="/food" element={<Food />} />
                    <Route path="/food/restaurant/:id" element={<FoodRestaurant />} />
                  </Route>
                </Route>
                <Route path="/login" element={<Login />} />
                <Route path="/recover-password" element={<RecoverPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/signup" element={<Navigate to="/register" replace />} />
                <Route path="/register/*" element={<Register />} />
                <Route path="/evento/:codigo" element={<EventoLanding />} />
                <Route path="/business/register" element={<BusinessRegister />} />
                <Route path="/driver/register" element={<DriverRegister />} />
                <Route path="/developers" element={<Developers />} />
                <Route path="/developers/*" element={<Developers />} />
                <Route path="/checkout" element={<EcommercePayment />} />
                <Route path="/pay" element={<EcommercePayment />} />
                <Route path="/interesse/:token" element={<InteresseEntidade />} />
                {/* PagaMe — only enabled in sandbox; disabled in production until ready */}
                {!process.env.REACT_APP_FIREBASE_PROJECT_ID?.includes('prod') && (
                  <Route path="/pagame/:code" element={<PagaMe />} />
                )}

                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<Main />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/wallet" element={<Wallet />} />
                    <Route path="/doacoes" element={<Doacoes />} />
                    <Route path="/payments/result" element={<PaymentResult />} />
                    <Route path="/payments/:categoriaId?/:categoriaName?" element={<Payments />} />
                    <Route path="/transactions" element={<Transactions />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/kyc" element={<KYC />} />
                    <Route path="/ecommerce/orders" element={<Orders />} />
                    <Route path="/ecommerce/bussiness/:paramId?" element={<Ecommerce />} />
                    <Route path="/eventos" element={<Eventos />} />
                    <Route path="/meus-bilhetes" element={<MeusBilhetes />} />
                    <Route path="/meus-downloads" element={<MeusDownloads />} />
                    <Route path="/reader/:id" element={<EbookReader />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/notificacoes" element={<Notifications />} />
                    <Route path="/vouchers" element={<Vouchers />} />
                    <Route path="/academia" element={<Academia />} />
                    <Route path="/hub" element={<Hub />} />
                    <Route path="/cashback" element={<Cashback />} />
                    <Route path="/pulseira" element={<Pulseira />} />
                    <Route path="/ajuda" element={<CentralDeAjuda />} />

                    {/* Banking Routes — only enabled in sandbox until BCV approval */}
                    {FEATURES.BANKING_HUB && (
                      <>
                        <Route path="/banking" element={<Banking />} />
                        <Route path="/banking/transfer" element={<BankingTransfer />} />
                        <Route path="/banking/bills" element={<BankingBills />} />
                      </>
                    )}

                    {/* Pagali Food — carrinho e pedidos pedem sessão; o interruptor do Admin decide (FoodGate) */}
                    <Route element={<FoodGate />}>
                      <Route path="/food/cart" element={<FoodCart />} />
                      <Route path="/food/orders" element={<FoodOrders />} />
                    </Route>

                    {/* Driver Routes (Pagali Driver - PFMOT) */}
                    <Route path="/driver" element={<DriverDashboard />} />
                    <Route path="/driver/delivery" element={<DriverDelivery />} />
                    <Route path="/driver/history" element={<DriverHistory />} />
                    <Route path="/driver/profile" element={<DriverProfile />} />

                    {/* Portal Routes (Business Users) */}
                    <Route path="/portal" element={<PortalDashboard />} />
                    <Route path="/portal/dashboard" element={<PortalDashboard />} />
                    <Route path="/portal/products" element={<PortalProducts />} />
                    <Route path="/portal/categories" element={<PortalCategories />} />
                    <Route path="/portal/orders" element={<PortalOrders />} />
                    <Route path="/portal/inventory" element={<PortalInventory />} />
                    <Route path="/portal/variations" element={<PortalVariations />} />
                    <Route path="/portal/announcements" element={<PortalAnnouncements />} />
                    <Route path="/portal/services" element={<PortalServices />} />
                    <Route path="/portal/payments" element={<PortalPayments />} />
                    <Route path="/portal/reports" element={<PortalReports />} />
                    <Route path="/portal/billing" element={<PortalBilling />} />
                    <Route path="/portal/events" element={<PortalEvents />} />
                    <Route path="/portal/donations" element={<PortalDonations />} />
                    <Route path="/portal/vouchers" element={<PortalVouchers />} />
                    <Route path="/portal/vouchers/scanner" element={<PortalVoucherScanner />} />
                    <Route path="/portal/api" element={<PortalApiDashboard />} />
                    <Route path="/portal/store-profile" element={<PortalStoreProfile />} />
                  </Route>
                </Route>

                {/* Redirects */}
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route
                  path="/privacy"
                  element={
                    <Navigate
                      to="https://pagali.cv/politica-de-privacidade"
                      replace
                    />
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <Navigate to="https://pagali.cv/termos-de-uso" replace />
                  }
                />

                {/* 404 - Redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
          </FoodCartProvider>
          </CartProvider>
        </UserProvider>
      </AuthProvider>
      </ToastProvider>
    </Router>
  );
};

export default App;
